import { useNode } from "@craftjs/core";
import React, { useEffect, useState } from "react";
import ReactPlayer from "react-player";
import styled from "styled-components";
import useApplyGlobalSettings, {
  CODEBLOCK_TYPES,
} from "../../../../hooks/useApplyGlobalSettings";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { colors } from "../../../../utils";
import { ASSETS } from "../../../../utils/config";
import TextView from "../../../TextView";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import VideoSettings from "./VideoSettings";
import { useSelector, useDispatch } from "react-redux";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";

const VideoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;
`;

const TextContainer = styled.div`
  background-color: #344054;
  padding: 8px;
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
`;

export const Video = ({
  src,
  alt,
  width,
  isApplyMarginOnAllSides,
  allSideMargin,
  margin,
  borderRadius,
  borderColor,
  borderWidth,
  showVideoTitle,
  title,
  hyperlink,
  showHyperlink,
  dropShadowColor,
}) => {
  const [showControls, setShowControls] = useState(false);

  const {
    connectors: { connect, drag },
    actions: { setProp },
  } = useNode();

  const selectedSegment = useSelector(getSelectedSegment);
  const selectedSegmentColor = selectedSegment?.settings?.color;
  const { getPaletteColor } = usePaletteColor();
  const { video: videoProps } = useSelector((state) => state.codeblockStyles);
  const dispatch = useDispatch();

  const marginStyle = isApplyMarginOnAllSides
    ? `${allSideMargin}px`
    : margin
    ? `${margin?.top}px ${margin?.right}px ${margin?.bottom}px ${margin?.left}px`
    : undefined;

  const border = borderColor
    ? `${borderWidth}px solid ${getPaletteColor(borderColor)}`
    : "none";

  // Initialize with default props from reducer for new components
  useEffect(() => {
    // Set the props from the reducer if they're undefined
    const timeoutId = setTimeout(() => {
      setProp((props) => {
        // Margin properties
        if (props.margin === undefined) {
          props.margin = { ...videoProps.margin };
        }

        if (props.isApplyMarginOnAllSides === undefined) {
          props.isApplyMarginOnAllSides = videoProps.isApplyMarginOnAllSides;
        }

        if (props.allSideMargin === undefined) {
          props.allSideMargin = videoProps.allSideMargin;
        }

        // Width property
        if (props.width === undefined) {
          props.width = videoProps.width;
        }

        // Border properties
        if (props.borderRadius === undefined) {
          props.borderRadius = videoProps.borderRadius;
        }

        if (props.borderWidth === undefined) {
          props.borderWidth = videoProps.borderWidth;
        }

        if (props.borderColor === undefined) {
          props.borderColor = videoProps.borderColor || "suggested_color_0";
        }

        // Other style properties
        if (props.dropShadowColor === undefined) {
          props.dropShadowColor = videoProps.dropShadowColor;
        }

        // Video-specific properties
        if (props.showVideoTitle === undefined) {
          props.showVideoTitle = videoProps.showVideoTitle;
        }

        if (props.hyperlink === undefined) {
          props.hyperlink = videoProps.hyperlink;
        }

        if (props.showHyperlink === undefined) {
          props.showHyperlink = videoProps.showHyperlink;
        }

        if (props.title === undefined) {
          props.title = videoProps.title;
        }
      });
    }, 5);

    // Cleanup timeout if the component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, []);

  // Use the hook to apply global settings when they change
  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.Video);

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        {src ? (
          <VideoContainer
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
            style={{
              padding: marginStyle,
            }}
          >
            <div
              style={{
                borderRadius,
                overflow: "hidden",
                border,
                cursor: showHyperlink ? "pointer" : "default",
                boxShadow:
                  dropShadowColor &&
                  `0px 0px 9.3px 0px ${getPaletteColor(dropShadowColor)}`,
                width: width,
                background: "transparent",
              }}
            >
              <ReactPlayer
                url={src}
                controls={true}
                width={"100%"}
                height={180}
                playing={false}
                config={{
                  youtube: {
                    playerVars: {
                      controls: 1,
                      showinfo: 1,
                      modestbranding: 1,
                      rel: 0,
                      disablekb: 0,
                      fs: 1,
                      playsinline: 1,
                    },
                  },
                }}
              />
              {showVideoTitle && !!title && (
                <TextContainer
                  style={{
                    width: "100%",
                  }}
                >
                  <TextView
                    variant="largeText"
                    style={{ color: colors.white, fontWeight: 500 }}
                  >
                    {title}
                  </TextView>
                </TextContainer>
              )}
            </div>
          </VideoContainer>
        ) : (
          <Image src={ASSETS.segmentEditor.UploadVideo} />
        )}
      </BlockIndicator>
    </div>
  );
};

export const VideoDefaultProps = {
  src: null,
  alt: "New video",
  margin: undefined,
  isApplyMarginOnAllSides: undefined,
  allSideMargin: undefined,
  width: undefined,
  borderRadius: undefined,
  borderWidth: undefined,
  title: "Untitled Video",
  showVideoTitle: undefined,
  hyperlink: undefined,
  showHyperlink: undefined,
  dropShadowColor: undefined,
  borderColor: undefined,
};

Video.craft = {
  props: VideoDefaultProps,
  related: {
    settings: VideoSettings,
  },
};
